import { usePathname, useRouter } from 'expo-router';
import React, { useState } from 'react';
import { ActivityIndicator, FlatList, Pressable, View } from 'react-native';

import ScanLink from '@/components/ScanLink';
import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import AppStyles from '@/constants/Styles';
import { useBackgroundBranches } from '@/hooks/useBackgroundFetch';
import { useThemeColor } from '@/hooks/useThemeColor';
import { Branch, useNavigationStore } from '@/stores/navigationStore';

export default function SelectBranchScreen() {
  const { getSelectedBranch, changeBranch } = useNavigationStore();
  const selectedBranch = getSelectedBranch();
  const [localSelectedBranch, setLocalSelectedBranch] = useState<Branch | null>(selectedBranch);
  const [isSelecting, setIsSelecting] = useState(false);
  const router = useRouter();
  const pathname = usePathname();

  // Theme-aware colors
  const tintColor = useThemeColor({}, 'tint');
  const errorColor = useThemeColor({}, 'errorText' as any);
  const cardBackgroundColor = useThemeColor({}, 'cardBackground' as any);
  const backgroundColor = useThemeColor({}, 'background') + "CC";

  // Use background fetch hook for branches
  const { data: branches, loading, error } = useBackgroundBranches();

  // Show loading state
  if (loading) {
    return (
      <ThemedView style={{ flex: 1 }}>
        <View style={[AppStyles.spinnerOverlay, { backgroundColor: `${backgroundColor}` }]}>
          <ActivityIndicator size="large" color={tintColor} />
        </View>
      </ThemedView>
    );
  }

  // Show error state
  if (error) {
    return (
      <ThemedView style={{ flex: 1 }}>
        <View style={[AppStyles.spinnerOverlay, { backgroundColor: `${backgroundColor}` }]}>
          <ThemedText style={{ color: errorColor, textAlign: 'center' }}>
            Error loading branches: {error.message}
          </ThemedText>
        </View>
      </ThemedView>
    );
  }

  // Show empty state
  if (!branches || branches.length === 0) {
    return (
      <ThemedView style={{ flex: 1 }}>
        <View style={[AppStyles.spinnerOverlay, { backgroundColor: `${backgroundColor}` }]}>
          <ThemedText style={{ textAlign: 'center' }}>
            No branches found.
          </ThemedText>
        </View>
      </ThemedView>
    );
  }

  // Show list for user to select
  return (
    <ThemedView style={{ flex: 1 }}>
      <FlatList
        data={branches}
        keyExtractor={(item) => item.branch}
        renderItem={({ item }) => (
          <Pressable
            style={[
              AppStyles.listItemLeftAligned, 
              { backgroundColor: cardBackgroundColor },
              localSelectedBranch?.branch === item.branch && AppStyles.selectedItem
            ]}
            disabled={isSelecting}
            onPress={async () => {
              try {
                setIsSelecting(true);
                setLocalSelectedBranch(item);
                await changeBranch(item);
                // Only navigate if we're on the main path
                if (pathname === '/' || pathname === '/(main)') {
                  router.replace(`/${item.branch_short_name}/campaign/`);
                }
              } catch (error) {
                console.error('Error selecting branch:', error);
                // Reset selection on error
                setLocalSelectedBranch(selectedBranch);
              } finally {
                setIsSelecting(false);
              }
            }}
          >
            <ThemedText style={AppStyles.headerText}>{item.branch}</ThemedText>
            {isSelecting && localSelectedBranch?.branch === item.branch && (
              <ActivityIndicator size="small" color={tintColor} style={{ marginLeft: 8 }} />
            )}
          </Pressable>
        )}
        ListHeaderComponent={<ThemedText style={[AppStyles.titleText, {paddingTop: 30}]}>Choose Branch</ThemedText>}
        ListFooterComponent={<ScanLink />}
      />
    </ThemedView>
  );
} 